<template>
  <div class="content">
    <div class="container-fluid">
      <div class="row justify-content-center">
        <!-- Main Configuration Card -->
        <div class="col-lg-8 col-md-10 mt-4">
          <div class="card border-0 shadow-sm">
             <div class="card-header bg-white border-0 pt-4 pb-2 text-center">
               <h3 class="font-weight-bold text-dark mb-0">Platform Configuration</h3>
               <p class="text-muted mt-2">Select providers to use for 'Day-Of' events</p>
             </div>
             
             <div class="card-body px-4 pb-5">
                <form @submit.prevent="activatePrimary">
                  
                  <div class="form-group mb-4">
                    <label class="d-block text-uppercase text-muted font-weight-bold small mb-3 letter-spacing-1">Integration Status</label>
                    <div class="row">
                        <div class="col-sm-6 mb-3" v-for="option in options" :key="option.value">
                            <div 
                               class="selection-card d-flex align-items-center p-3 rounded h-100"
                               :class="selectedOptions.includes(option.value) ? 'active-card' : 'inactive-card'"
                               @click="toggleOption(option.value)"
                            >
                                <div class="icon-container mr-3 d-flex align-items-center justify-content-center rounded-circle"
                                     :class="selectedOptions.includes(option.value) ? 'bg-white text-primary' : 'bg-light text-muted'"
                                >
                                    <i class="fas fa-parking" v-if="option.value === 'parkwhiz'"></i>
                                    <i class="fas fa-mobile-alt" v-else-if="option.value === 'parkmobile'"></i>
                                    <i class="fas fa-search-location" v-else-if="option.value === 'spothero'"></i>
                                    <i class="fas fa-car" v-else></i>
                                </div>
                                <div class="flex-grow-1">
                                    <h5 class="mb-0 font-weight-bold">{{ option.label }}</h5>
                                    <small :class="selectedOptions.includes(option.value) ? 'text-white-50' : 'text-muted'">
                                        {{ selectedOptions.includes(option.value) ? 'Active' : 'Disabled' }}
                                    </small>
                                </div>
                                <div class="status-toggle">
                                    <i class="fas fa-check-circle fa-lg" v-if="selectedOptions.includes(option.value)"></i>
                                    <i class="far fa-circle fa-lg text-muted" v-else></i>
                                </div>
                            </div>
                        </div>
                    </div>
                  </div>

                  <div class="mt-4">
                     <button 
                        type="submit" 
                        :disabled="isLoading" 
                        class="btn btn-primary btn-lg btn-block shadow-sm font-weight-bold py-3"
                        style="border-radius: 12px; font-size: 1.1rem;"
                     >
                        <i class="fas fa-save mr-2" v-if="!isLoading"></i>
                        <i class="fas fa-spinner fa-spin mr-2" v-else></i>
                        {{ isLoading ? 'Applying Changes...' : 'Save Configuration' }}
                     </button>
                  </div>
                </form>

                <transition name="fade">
                    <div v-if="message" class="alert mt-4 shadow-sm border-0 d-flex align-items-center" :class="success ? 'alert-success' : 'alert-danger'">
                        <i class="fas fa-2x mr-3" :class="success ? 'fa-check-circle' : 'fa-exclamation-triangle'"></i>
                        <div>
                            <h6 class="alert-heading font-weight-bold mb-0">{{ success ? 'Success' : 'Error' }}</h6>
                            <p class="mb-0 small">{{ message }}</p>
                        </div>
                    </div>
                </transition>

             </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ProcessingConfig",
  data() {
    return {
      options: [
        { label: "ParkWhiz", value: "parkwhiz" },
        { label: "ParkMobile", value: "parkmobile" },
        { label: "SpotHero", value: "spothero" },
        { label: "JustPark", value: "justpark" }
      ],
      selectedOptions: [],
      isLoading: false,
      message: "",
      success: false,
      activePrimaries: [],
    };
  },
  methods: {
    toggleOption(value) {
        if (this.selectedOptions.includes(value)) {
            this.selectedOptions = this.selectedOptions.filter(item => item !== value);
        } else {
            this.selectedOptions.push(value);
        }
    },
    async activatePrimary() {
      this.isLoading = true;
      this.message = "";
      try {
        const allTargets = this.options
          .map(opt => opt.value);

        const results = [];
        
        for (const target of allTargets) {
          const shouldActivate = this.selectedOptions.includes(target);
          
          const response = await fetch("https://api.spotjockeys.com/api/activate-primary", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ target_name: target, activate_all: shouldActivate }),
          });
          
          if (!response.ok) {
            results.push({ target, success: false });
          } else {
            results.push({ target, success: true });
          }
        }
        
        const failedTargets = results.filter(r => !r.success);
        if (failedTargets.length === 0) {
          this.message = "System configuration has been successfully updated.";
          this.success = true;
          // Auto hide success message
          setTimeout(() => { this.message = '' }, 3000);
        } else {
          this.message = `Update partial. Failed: ${failedTargets.map(r => r.target).join(', ')}`;
          this.success = false;
        }
        
        await this.fetchActivePrimaries(); 
      } catch (error) {
        this.message = error.message || "An unexpected error occurred.";
        this.success = false;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchActivePrimaries() {
      try {
        const response = await fetch("https://api.spotjockeys.com/api/active-primary");
        if (!response.ok) throw new Error("Failed to fetch status");
        this.activePrimaries = await response.json();

        // Populate selectedOptions based on activePrimaries
        this.selectedOptions = this.activePrimaries
          .filter(record => record.is_active)
          .map(record => record.primary_name.toLowerCase());
      } catch (error) {
        this.activePrimaries = [];
        this.message = "Could not load current configuration.";
        this.success = false;
      }
    },
  },
  mounted() {
    this.fetchActivePrimaries();
  },
};
</script>

<style scoped>
.selection-card {
    border: 2px solid #e9ecef;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
    min-height: 100px;
}

.selection-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

.selection-card:active {
    transform: translateY(0);
}

.active-card {
    background: linear-gradient(135deg, #4e73df 0%, #224abe 100%);
    border-color: #4e73df;
    color: white;
}

.inactive-card {
    background-color: #fff;
    color: #5a5c69;
}

.icon-container {
    width: 48px;
    height: 48px;
    font-size: 20px;
}

.letter-spacing-1 {
    letter-spacing: 1px;
}

/* Animations */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>